import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-blue-600 text-white shadow-lg hover:bg-blue-500 focus-visible:outline-blue-600",
        destructive: "bg-red-500 text-white shadow-lg hover:bg-red-600 focus-visible:outline-red-500",
        outline: "border border-gray-200 bg-white shadow-lg hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-gray-950",
        secondary: "bg-gray-100 text-gray-900 shadow-lg hover:bg-gray-200 focus-visible:outline-gray-950",
        ghost: "hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-gray-950",
        link: "text-blue-600 underline-offset-4 hover:underline focus-visible:outline-blue-500",
      },
      size: {
        default: "h-10 px-8 py-4",
        sm: "h-9 px-6 py-3",
        lg: "h-11 px-10 py-5",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }